import React, { useState, useEffect } from "react";
import EventIcon from "@mui/icons-material/Event";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import LoadingButton from "@mui/lab/LoadingButton";
import Drawer from "@mui/material/Drawer";
import CloseIcon from "@mui/icons-material/Close";
import TextField from "@mui/material/TextField";
import SaveIcon from "@mui/icons-material/Save";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import NoteAltOutlinedIcon from "@mui/icons-material/NoteAltOutlined";
import LocalActivityIcon from "@mui/icons-material/LocalActivity";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import DeleteOutlinedIcon from "@mui/icons-material/DeleteOutlined";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import AddIcon from "@mui/icons-material/Add";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Chip from "@mui/material/Chip";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { get_t_str, text_to_num } from "../../../../helpers/helpers";
import DrawerHeader from "../../requests/shared/helpers/DrawerHeader";
import { formatDateReadable } from "../../requests/utilities/helpers";
import { generateTimeOptions } from "../../../../helpers/helpers";
import EventNoteAttachments from "./EventNoteAttachments";
import { toast } from "react-toastify";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import FormControl from "@mui/material/FormControl";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";

// Thumbnail for a staged (not-yet-uploaded) file: shows an image preview via an
// object URL, or a generic file icon for non-images.
function PendingThumb({ file }) {
  const [url, setUrl] = React.useState(null);
  React.useEffect(() => {
    if (file && file.type && file.type.startsWith("image/")) {
      const objectUrl = URL.createObjectURL(file);
      setUrl(objectUrl);
      return () => URL.revokeObjectURL(objectUrl);
    }
    setUrl(null);
  }, [file]);

  if (url) {
    return (
      <img
        src={url}
        alt={file.name}
        className="w-9 h-9 rounded object-cover shrink-0"
      />
    );
  }
  return (
    <InsertDriveFileIcon className="text-slate-400 shrink-0" style={{ fontSize: 20 }} />
  );
}

export default function WeatherEventsCard(props) {
  // Shows modal when set to true
  const [open, setOpen] = React.useState(false);

  const [eventTitle, setEventTitle] = React.useState("");
  const [eventDescription, setEventDescription] = React.useState("");
  const [eventDate, setEventDate] = React.useState(props.date_str);
  const [eventStartTime, setEventStartTime] = React.useState("");
  const [eventEndTime, setEventEndTime] = React.useState("");
  const [visibleToEmployees, setVisibleToEmployees] = React.useState(true);
  const [editingEventId, setEditingEventId] = React.useState(null);

  // Files staged on a brand-new note. They are only uploaded + attached once
  // the note is saved (so cancelling never leaves orphaned uploads).
  const [pendingFiles, setPendingFiles] = React.useState([]);
  const fileInputRef = React.useRef(null);

  const eventCompanyId = props.eventAttachmentProps?.companyId;
  const canAttach = Boolean(eventCompanyId && props.eventAttachmentProps?.attachEventFile);

  const [buttonPressed, setButtonPressed] = React.useState(false);
  const [isEventsDisplayed, setIsEventsDisplayed] = React.useState(true);

  function toggleEventsDisplayed() {
    setIsEventsDisplayed((prevState) => !prevState);
  }

  const [showNoteInputs, setShowNoteInputs] = React.useState(false);

  function toggleShowNoteInputs() {
    setShowNoteInputs((prevState) => !prevState);
  }

  // Modal style object
  const style = {
    position: "absolute",
    top: "50%",
    left: "50%",
    transform: "translate(-50%, -50%)",
    width: 600,
    bgcolor: "background.paper",
    border: "2px solid #E2E8F0",
    borderRadius: "15px",
    boxShadow: 24,
    p: 4,
  };

  let weatherEmoji = "";
  let degrees = "";

  if (props.daily_weather?.length > 0) {
    let text = props.daily_weather?.[0].text;
    degrees = props.daily_weather?.[0].maxtemp;

    if (text === "Sunny") {
      weatherEmoji = "☀️";
    } else if (text === "Partly cloudy") {
      weatherEmoji = "🌤";
    } else if (text === "Cloudy") {
      weatherEmoji = "☁️";
    } else if (text === "Rain") {
      weatherEmoji = "🌧";
    } else if (text === "Snow") {
      weatherEmoji = "❄️";
    } else if (text === "Thunderstorms") {
      weatherEmoji = "🌩";
    } else {
      weatherEmoji = "";
    }
  }

  function get_weather_path(icon_string) {
    return process.env.PUBLIC_URL + "/images/weather/" + icon_string.slice(-7);
  }

  // Function for opening the modal
  const handleOpen = () => {
    setOpen(true);
  };

  const handleStart = async () => {
    const data = {
      title: eventTitle.trim(),
      description: eventDescription ? eventDescription.trim() : "",
      date: eventDate,
      start_time: eventStartTime ? text_to_num(eventStartTime) : null,
      end_time: eventEndTime ? text_to_num(eventEndTime) : null,
      visible_to_employees: visibleToEmployees,
    };

    setButtonPressed(true);

    try {
      if (editingEventId) {
        // Update existing event (attachments on existing notes are managed
        // via the per-note card widget below).
        await props.updateEvent(editingEventId, data);
      } else {
        // Create new event; saveEvent uploads + attaches the staged files and
        // refreshes the list once, so the note shows up with its attachments.
        // It only rejects if the note itself failed to save — attachment
        // failures come back as a partial flag so we don't mislabel a saved
        // note as failed (which would risk a duplicate on retry).
        const result = await props.saveEvent(
          data,
          canAttach ? pendingFiles : [],
          eventCompanyId
        );
        setPendingFiles([]);
        if (result?.attachmentsFailed) {
          toast.warning("Note saved, but some attachments couldn't be added.");
        }
      }
    } catch (error) {
      toast.error("Failed to save note");
      setButtonPressed(false);
    }
  };

  const handleAddPendingFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = "";
    if (file) setPendingFiles((prev) => [...prev, file]);
  };

  const removePendingFile = (index) => {
    setPendingFiles((prev) => prev.filter((_, i) => i !== index));
  };

  // Function for closing the modal
  const handleClose = () => {
    setButtonPressed(false);
    props.setOpenNotes(false);
    setEventTitle("");
    setEventDescription("");
    setEventStartTime("");
    setEventEndTime("");
    setVisibleToEmployees(true);
    setEditingEventId(null);
    setPendingFiles([]);
    props.setSaveEventFinished(false);
    setIsEventsDisplayed(true);
    setShowNoteInputs(false);
  };

  const handleEditEvent = (event) => {
    // Populate form with event data
    setEventTitle(event.title || "");
    setEventDescription(event.description || "");
    setEventDate(event.date || props.date_str);
    setEventStartTime(event.start_time ? get_t_str(event.start_time) : "");
    setEventEndTime(event.end_time ? get_t_str(event.end_time) : "");
    setVisibleToEmployees(event.visible_to_employees !== false);
    setEditingEventId(event.id);
    setShowNoteInputs(true);
    setIsEventsDisplayed(false);
  };

  useEffect(() => {
    setEventDate(props.date_str);
  }, [props.date_str]);

  useEffect(() => {
    if (props.saveEventFinished) {
      setButtonPressed(false);
      setEventTitle("");
      setEventDescription("");
      setEventStartTime("");
      setEventEndTime("");
      setVisibleToEmployees(true);
      setEditingEventId(null);
      setPendingFiles([]);
      props.setSaveEventFinished(false);
      setIsEventsDisplayed(true);
      setShowNoteInputs(false);
    }
  }, [props.saveEventFinished]);

  const checkIfValid = () => {
    if (eventTitle && eventTitle.trim().length > 0) {
      return true;
    } else {
      return false;
    }
  };

  return (
    <>
      {props.element === "weather" ? (
        ((!props.minimizeMode && !(props.isFixed && props.isHeaderCollapsed)) || props.view === "daily") && (
          <div>
            {props.daily_weather?.length > 0 ? (
              <div className="flex items-center -ml-0.5">
                <img
                  src={get_weather_path(props.daily_weather?.[0].icon)}
                  className="d-inline-block align-top"
                  alt={props.daily_weather?.[0].text}
                  style={{ height: props.size === "lg" ? "24px" : "18px" }}
                />
                <span className={`${props.size === "lg" ? "text-sm" : "text-[12px]"} ml-1 text-slate-500`}>
                  {typeof degrees === "number" ? Math.round(degrees) : degrees}
                  °F
                </span>
              </div>
            ) : (
              <div></div>
            )}
          </div>
        )
      ) : props.element === "notes" ? (
        <div>
          <div className="text-slate-500 flex items-center gap-1.5">
            {/* User-created notes - purple icon */}
            {props.daily_events?.length > 0 ? (
              <div>
                <Tooltip
                  title={
                    <div>
                      <div className="space-y-3">
                        {props.daily_events.map((event, i) => (
                          <div key={i}>
                            <div className="text-slate-900">
                              <p className="text-sm font-semibold">{event.title}</p>
                              {event.description && (
                                <p className="text-xs text-slate-600 mt-0.5">{event.description}</p>
                              )}
                              {event.start_time && (
                                <p className="text-xs text-violet-600 mt-0.5">
                                  {get_t_str(event.start_time)}{event.end_time && ` - ${get_t_str(event.end_time)}`}
                                </p>
                              )}
                              {event.visible_to_employees === false && (
                                <Chip
                                  size="small"
                                  icon={<VisibilityOffIcon fontSize="small" />}
                                  label="Hidden from employees"
                                  sx={{
                                    mt: 1,
                                    backgroundColor: "#fef3c7",
                                    color: "#92400e",
                                    '& .MuiChip-icon': { color: "#92400e" },
                                  }}
                                />
                              )}
                            </div>
                            {i < props.daily_events.length - 1 && (
                              <div className="mt-3 h-px bg-slate-200" />
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  }
                  arrow
                  componentsProps={{
                    tooltip: {
                      sx: {
                        maxHeight: "400px",
                        overflow: "auto",
                        backgroundColor: "white",
                        padding: "12px 20px",
                        borderRadius: "8px",
                        boxShadow: "0 20px 25px -5px rgb(0 0 0 / 0.1), 0 8px 10px -6px rgb(0 0 0 / 0.1)",
                        border: "1px solid #e2e8f0",
                        color: "inherit",
                        maxWidth: "400px",
                      },
                    },
                    arrow: { sx: { color: "white" } },
                  }}
                >
                  <button
                    type="button"
                    aria-label="Notes"
                    onClick={() => props.handleOpenNotes()}
                    className="relative w-[30px] h-[30px] inline-flex items-center justify-center rounded-lg border border-violet-200 bg-violet-50 text-violet-700 hover:bg-violet-100 transition-colors duration-150"
                  >
                    <span className="absolute -top-1.5 -right-1.5 min-w-[16px] h-4 px-1 rounded-full bg-violet-700 text-white text-[10px] font-bold leading-none inline-flex items-center justify-center">
                      {props.daily_events?.length}
                    </span>
                    <NoteAltOutlinedIcon style={{ fontSize: "16px" }} />
                  </button>
                </Tooltip>
              </div>
            ) : (
              <div>
                <Tooltip title="Add Note">
                  <button
                    type="button"
                    aria-label="Add note"
                    onClick={() => props.handleOpenNotes()}
                    className="w-[30px] h-[30px] inline-flex items-center justify-center rounded-lg border border-slate-200 bg-white text-slate-500 hover:text-slate-700 hover:border-slate-300 transition-colors duration-150"
                  >
                    <NoteAltOutlinedIcon style={{ fontSize: "16px" }} />
                  </button>
                </Tooltip>
              </div>
            )}

            {/* Local events from web search - emerald green icon */}
            {props.daily_local_events?.length > 0 && (
              <div className="-mr-2">
                <Tooltip
                  title={`${props.daily_local_events.length} local event${props.daily_local_events.length > 1 ? 's' : ''}`}
                >
                  <IconButton
                    className="relative"
                    style={{
                      borderRadius: "10px"
                    }}
                  >
                    <div className="min-w-[18px] h-[18px] bg-gradient-to-r from-emerald-500 to-green-600 rounded-full flex items-center justify-center absolute top-0 right-0 px-1 shadow-md">
                      <span className="text-white text-[10px] font-bold leading-none">{props.daily_local_events.length}</span>
                    </div>
                    <LocalActivityIcon style={{ color: "#10b981" }} />
                  </IconButton>
                </Tooltip>
              </div>
            )}
          </div>
        </div>
      ) : (
        <div className="w-full flex items-center justify-between">
          {props.daily_weather?.length > 0 ? (
            <div className="flex items-center">
              <img
                src={get_weather_path(props.daily_weather?.[0].icon)}
                className="d-inline-block align-top"
                alt={props.daily_weather[0].text}
                style={{ height: "24px" }}
              />
              <span className="text-xs ml-1 text-gray-700">{degrees}°F</span>
            </div>
          ) : (
            <div></div>
          )}
          <div className="text-slate-500">
            {props.daily_events?.length > 0 ? (
              <div className="-mr-2">
                <Tooltip
                  title={
                    <div className="p-3 ">
                      <div className="font-bold text-lg">Notes</div>
                      <ul className="tooltip-list mt-2">
                        {props.daily_events.map((e, i) => (
                          <li key={i} className="flex flex-col">
                            <span>{"- " + e.title}</span>
                            {e.visible_to_employees === false && (
                              <span className="text-[11px] text-amber-700 ml-2 mt-1 flex items-center gap-1">
                                <VisibilityOffIcon sx={{ fontSize: 14 }} /> Hidden from employees
                              </span>
                            )}
                          </li>
                        ))}
                      </ul>
                    </div>
                  }
                  arrow
                >
                  <IconButton onClick={() => props.handleOpenNotes()}>
                    <EventIcon color="primary" style={{ fontSize: "18px" }} />
                  </IconButton>
                </Tooltip>
              </div>
            ) : (
              <div className="-mr-2">
                <Tooltip title="Add Note">
                  <IconButton onClick={() => props.handleOpenNotes()}>
                    <CalendarTodayIcon
                      color="default"
                      style={{ fontSize: "17px" }}
                    />
                  </IconButton>
                </Tooltip>
              </div>
            )}
          </div>
        </div>
      )}
      <Drawer anchor="right" open={props.openNotes} onClose={handleClose}>
        <div className="w-[450px] h-full">
          <div className="max-h-screen overflow-auto">
            <DrawerHeader
              title={"Notes on " + formatDateReadable(eventDate)}
              onClose={handleClose}
            />
            <div className="w-full px-6">
              {props.daily_events?.length === 0 ? (
                <div>
                  <p className="text-slate-900">No note added</p>
                  <p className="text-slate-500 text-sm mt-1">
                    Click the button below to add a note. Notes will show up under your team's shifts.
                  </p>
                </div>
              ) : (
                <div>
                  {props.daily_events?.map((event, i) => (
                    <div key={i} className="border border-slate-200 rounded-xl px-5 py-4 mb-4 mt-6 bg-white shadow-sm hover:shadow-md transition-shadow">
                      <div className="flex items-start justify-between gap-4">
                        <h4 className="min-w-0 pt-1 text-lg font-semibold leading-6 text-slate-900">
                          {event.title}
                        </h4>
                        <div className="flex shrink-0 gap-2">
                          <button
                            type="button"
                            aria-label={`Edit ${event.title || "note"}`}
                            className="flex h-9 w-9 items-center justify-center rounded-lg bg-violet-50 text-violet-600 transition-colors hover:bg-violet-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-violet-500 focus-visible:ring-offset-2"
                            onClick={() => handleEditEvent(event)}
                          >
                            <EditOutlinedIcon style={{ fontSize: "20px" }} />
                          </button>
                          <button
                            type="button"
                            aria-label={`Delete ${event.title || "note"}`}
                            className="flex h-9 w-9 items-center justify-center rounded-lg bg-slate-50 text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-slate-400 focus-visible:ring-offset-2"
                            onClick={() => props.deleteEvent(event.id)}
                          >
                            <DeleteOutlinedIcon style={{ fontSize: "20px" }} />
                          </button>
                        </div>
                      </div>

                      {(event.start_time || event.visible_to_employees === false) && (
                        <div className="mt-3 flex flex-wrap items-center gap-2">
                          {event.start_time && (
                            <div className="flex h-7 w-fit items-center gap-1.5 rounded-full bg-violet-50 px-3 text-xs font-medium text-violet-600">
                              <AccessTimeIcon style={{ fontSize: "15px" }}/>
                              <span>
                                {get_t_str(event.start_time)}{" "}
                                {event.end_time && `- ${get_t_str(event.end_time)}`}
                              </span>
                            </div>
                          )}
                          {event.visible_to_employees === false && (
                            <Chip
                              size="small"
                              icon={<VisibilityOffIcon />}
                              label="Hidden from employees"
                              sx={{
                                height: "28px",
                                backgroundColor: "#fef3c7",
                                color: "#92400e",
                                fontSize: "12px",
                                fontWeight: 500,
                                '& .MuiChip-icon': {
                                  color: "#92400e",
                                  fontSize: "16px",
                                },
                              }}
                            />
                          )}
                        </div>
                      )}

                      {event.description && (
                        <p className="mt-4 text-sm leading-5 text-slate-600">
                          {event.description}
                        </p>
                      )}
                      <EventNoteAttachments
                        event={event}
                        companyId={props.eventAttachmentProps?.companyId}
                        attachEventFile={props.eventAttachmentProps?.attachEventFile}
                        removeEventFile={props.eventAttachmentProps?.removeEventFile}
                        deleteUploadedFile={props.eventAttachmentProps?.deleteEventUpload}
                      />
                    </div>
                  ))}
                </div>
              )}
              {!showNoteInputs ? (
                <div
                  className="w-full bg-violet-100 hover:bg-violet-200 text-violet-700 text-sm flex items-center justify-center space-x-1 py-2.5 mt-5 rounded-lg cursor-pointer"
                  onClick={toggleShowNoteInputs}
                >
                  <p>Add note</p>
                  <div>
                    <AddIcon style={{ fontSize: "20px" }} />
                  </div>
                </div>
              ) : (
                <div className="w-full mt-5 border-t border-slate-300">
                  <div className="mt-5">
                    <div>
                      <p className="text-sm text-slate-500">Title*</p>
                      <div className="mt-1">
                        <TextField
                          placeholder="e.g. Private party"
                          fullWidth
                          variant="outlined"
                          size="small"
                          value={eventTitle}
                          onChange={(e) => setEventTitle(e.target.value)}
                          InputProps={{
                            sx: {
                              backgroundColor: "white",
                              borderRadius: "8px",
                              padding: "5px",
                              height: "45px",
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "#6d28d9",
                                },
                            },
                          }}
                        />
                      </div>
                    </div>
                    <div className="mt-5">
                      <p className="text-sm text-slate-500">Description</p>
                      <div className="mt-1">
                        <TextField
                          placeholder="Add a description (optional)"
                          multiline
                          rows={3}
                          className="w-full"
                          value={eventDescription}
                          onChange={(e) => setEventDescription(e.target.value)}
                          InputProps={{
                            sx: {
                              borderRadius: "8px",
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                {
                                  borderColor: "#6d28d9",
                                },
                            },
                          }}
                        />
                      </div>
                    </div>
                    <div className="flex items-center mt-5">
                      <div className="w-[42%]">
                        <p className="text-sm text-slate-500">Start time</p>
                        <div className="mt-1">
                          <FormControl fullWidth size="small">
                            <Select
                              value={eventStartTime}
                              name="start_time"
                              onChange={(e) =>
                                setEventStartTime(e.target.value)
                              }
                              sx={{
                                backgroundColor: "white",
                                borderRadius: "8px",
                                padding: "5px",
                                height: "45px",
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                  {
                                    borderColor: "#6d28d9",
                                  },
                              }}
                            >
                              {generateTimeOptions(0).map((time) => (
                                <MenuItem key={time} value={time}>
                                  {time}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </div>
                      </div>
                      <div className="w-[16%] text-slate-500 flex items-center justify-center mt-6">
                        <ArrowForwardIcon />
                      </div>
                      <div className="w-[42%]">
                        <p className="text-sm text-slate-500">End time</p>
                        <div className="mt-1">
                          <FormControl fullWidth size="small">
                            <Select
                              value={eventEndTime}
                              name="end_time"
                              onChange={(e) => setEventEndTime(e.target.value)}
                              sx={{
                                backgroundColor: "white",
                                borderRadius: "8px",
                                padding: "5px",
                                height: "45px",
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                  {
                                    borderColor: "#6d28d9",
                                  },
                              }}
                            >
                              {generateTimeOptions(0).map((time) => (
                                <MenuItem key={time} value={time}>
                                  {time}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </div>
                      </div>
                    </div>
                    <FormControlLabel
                      sx={{ marginTop: "16px" }}
                      control={
                        <Switch
                          checked={visibleToEmployees}
                          onChange={(e) =>
                            setVisibleToEmployees(e.target.checked)
                          }
                          sx={{
                            '& .MuiSwitch-switchBase.Mui-checked': {
                              color: '#7c3aed',
                            },
                            '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                              backgroundColor: '#7c3aed',
                            },
                            '& .MuiSwitch-track': {
                              backgroundColor: '#c4b5fd',
                            },
                          }}
                        />
                      }
                      label="Show in employee app"
                    />
                    {!editingEventId && canAttach && (
                      <div className="mt-5">
                        <div className="flex items-center justify-between">
                          <p className="text-sm text-slate-500">Attachments</p>
                          <button
                            className="flex items-center gap-1 text-sm font-medium text-violet-700 hover:text-violet-800"
                            onClick={() =>
                              fileInputRef.current && fileInputRef.current.click()
                            }
                          >
                            <AttachFileIcon style={{ fontSize: 16 }} />
                            Add file
                          </button>
                        </div>
                        <input
                          ref={fileInputRef}
                          type="file"
                          className="hidden"
                          onChange={handleAddPendingFile}
                        />
                        {pendingFiles.length > 0 ? (
                          <div className="mt-2 space-y-1.5">
                            {pendingFiles.map((file, i) => (
                              <div
                                key={i}
                                className="flex items-center gap-2 border border-slate-200 rounded-lg px-2.5 py-2"
                              >
                                <PendingThumb file={file} />
                                <span className="flex-1 min-w-0 text-sm text-slate-700 truncate">
                                  {file.name}
                                </span>
                                <button
                                  className="flex items-center text-slate-400 hover:text-red-600 shrink-0"
                                  onClick={() => removePendingFile(i)}
                                  title="Remove"
                                >
                                  <CloseIcon style={{ fontSize: 16 }} />
                                </button>
                              </div>
                            ))}
                          </div>
                        ) : (
                          <p className="text-xs text-slate-400 mt-1">
                            Add photos or files to this note.
                          </p>
                        )}
                      </div>
                    )}
                    <div className="w-full mt-5">
                      <LoadingButton
                        fullWidth
                        variant="contained"
                        loading={buttonPressed}
                        disabled={!checkIfValid()}
                        loadingPosition="end"
                        endIcon={<SaveIcon />}
                        onClick={() => handleStart()}
                        sx={{
                          width: "100%",
                          textTransform: "none",
                          borderRadius: "8px",
                          color: "#6d28d9",
                          backgroundColor: "#ede9fe",
                          boxShadow: "none",
                          "&:hover": {
                            backgroundColor: "#ddd6fe",
                            boxShadow: "none",
                          },
                          fontSize: "16px",
                        }}
                      >
                        {editingEventId ? "Update Note" : "Save Note"}
                      </LoadingButton>
                    </div>
                  </div>
                </div>
              )}
            </div>
            <div className="h-[80px]"></div>
          </div>
        </div>
      </Drawer>
    </>
  );
}
